import {
  Outlet,
  Link,
  useLocation,
  useNavigate,
  Navigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { useEffect, useState } from "react";
import { fetchSpaceDataWithAuth } from "services/addSpace.services";
import { useLanguage } from "context";
import { useSelector } from "react-redux";
import { selectAuthToken } from "store/auth/selectors";

const AddSpaceLayout = () => {
  const [lastStep, setLastStep] = useState(0);
  const [hasFetchedInitialData, setHasFetchedInitialData] = useState(false);
  const { spaceId } = useParams();
  const location = useLocation();
  const { t } = useTranslation("global");
  const { language } = useLanguage();
  const navigate = useNavigate();
  const accessToken = useSelector(selectAuthToken);

  // Step configuration
  const steps = [
    {
      step: 1,
      title: `1. ${t("provide.step1.title")}`,
      path: spaceId ? `/create-space/step1/${spaceId}` : `/create-space/step1`,
    },
    {
      step: 2,
      title: `2. ${t("provide.step2.title")}`,
      path: `/create-space/step2/${spaceId}`,
    },
    {
      step: 3,
      title: `3. ${t("provide.step3.title")}`,
      path: `/create-space/step3/${spaceId}`,
    },
    {
      step: 4,
      title: `4. ${t("provide.step4.title")}`,
      path: `/create-space/step4/${spaceId}`,
    },
  ];

// Match only 'fr', 'en', 'nl', 'de', 'es' etc.
const pathWithoutLanguage = location.pathname.replace(/^\/(fr|en|nl|de|es)\//, '/');
const currentStepIndex = steps.findIndex((step) =>
  pathWithoutLanguage.startsWith(step.path)
);

  // Fetch existing space data when editing
  useEffect(() => {
    const fetchSpaceData = async () => {
      if (spaceId) {
        const data = await fetchSpaceDataWithAuth(
          spaceId,
          accessToken,
          language
        );
        setLastStep(data.last_step || 1); // Use server's last_step
        setHasFetchedInitialData(true);

        const allowedStep = data.last_step + 1;
        if (currentStepIndex + 1 > allowedStep) {
          navigate(steps[allowedStep - 1].path);
        }
      } else {
        setLastStep(0); // Explicitly set for new spaces
      }
    };
    fetchSpaceData();
  }, [spaceId]);

  useEffect(() => {
    if (spaceId && hasFetchedInitialData) {
      const allowedStep = lastStep + 1;
      if (currentStepIndex + 1 > allowedStep) {
        setLastStep(currentStepIndex);
      }
    }
  }, [currentStepIndex, lastStep, spaceId, hasFetchedInitialData]);

  return (
    <div className={`${styles.addSpaceContainer} pageContainer`}>
      {/* Steps Navigation */}
      {/* Steps Navigation */}
      <div className={styles.path}>
        <a href="/">{t("home.title")} / </a>
        <span>{t("provide.title")}</span>
      </div>

      <div className={styles.stepsProgress}>
        {steps.map((step, index) => {
          const isCompleted = step.step <= lastStep;
          const isCurrent = step.step === currentStepIndex + 1;
          const isAllowed = step.step <= lastStep + 1;

          return (
            <Link
              key={step.step}
              to={isAllowed ? step.path : "#"}
              className={`${styles.stepItem} 
                              ${isCompleted ? styles.completed : ""}
                ${isCurrent ? styles.active : ""}
                ${!isAllowed ? styles.disabled : styles.allowed}
              `}
              onClick={!isAllowed ? (e) => e.preventDefault() : undefined}
            >
              {step.title}
            </Link>
          );
        })}
      </div>

      {/* Step Content */}
      <div className={styles.stepContent}>
        <Outlet context={{ lastStep, setLastStep }} />
      </div>
    </div>
  );
};

export default AddSpaceLayout;
